/**
 * Senior Level Home Category Navigation & Subcategory Panel Service
 */

class CategoryPanelManager {
    constructor() {
        this.elements = {};
        this.activeCategoryId = null;
        this.cachedBounds = null;
        this.mouseMoveScheduled = false;
    }

    init() {
        this.cacheElements();
        this.bindEvents();
        this.registerGlobalHooks();
    }

    cacheElements() {
        this.elements = {
            wrapper: document.querySelector('.categories-section'),
            panel: document.getElementById('subcategory-panel'),
            title: document.getElementById('subcategory-panel-title'),
            body: document.getElementById('subcategory-panel-body'),
            actions: document.getElementById('subcategory-panel-actions'),
            promoLink: document.getElementById('subcategory-promo-link'),
            categoryContainer: document.getElementById('category'),
            categoryActiveBar: document.getElementById('category-active-bar'),
            carouselActiveBar: document.getElementById('carousel-active-bar'),
            carouselCol: document.getElementById('carousel-col'),
        };
    }

    updateCachedBounds() {
        const { wrapper, panel } = this.elements;
        if (!wrapper || !panel || panel.classList.contains('hidden')) {
            this.cachedBounds = null;
            return;
        }

        const wrapRect = wrapper.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();

        this.cachedBounds = {
            left: Math.min(wrapRect.left, panelRect.left) - 100,
            right: Math.max(wrapRect.right, panelRect.right) + 100,
            top: Math.min(wrapRect.top, panelRect.top) - 50,
            bottom: Math.max(wrapRect.bottom, panelRect.bottom) + 50,
        };
    }

    registerGlobalHooks() {
        window.__categoryClick = (item) => {
            if (item?.slug) window.location.href = `/${item.slug}`;
        };

        window.__categoryHover = (item) => this.handleCategoryHover(item);
        window.__openSubcategoryPanel = (cat) => this.openPanel(cat);
        window.__closeSubcategoryPanel = () => this.closePanel();
        window.__scrollSubcategories = (dir) => this.scrollSubcategories(dir);
    }

    bindEvents() {
        // Close panel when clicking outside
        document.addEventListener('click', (e) => {
            const { panel, categoryContainer } = this.elements;
            if (panel?.getAttribute('data-open') !== 'true') return;

            const isInside = panel.contains(e.target) || categoryContainer?.contains(e.target);
            if (!isInside) this.closePanel();
        }, true);

        // Update cached bounds on resize
        window.addEventListener('resize', () => {
            if (this.elements.panel?.getAttribute('data-open') === 'true') {
                this.updateCachedBounds();
            }
        }, { passive: true });

        // Close panel when cursor moves beyond safety boundary (throttled with rAF + cached bounds)
        document.addEventListener('mousemove', (e) => {
            const { panel } = this.elements;
            if (!panel || panel.classList.contains('hidden')) return;

            if (!this.mouseMoveScheduled) {
                this.mouseMoveScheduled = true;
                requestAnimationFrame(() => {
                    this.mouseMoveScheduled = false;
                    if (!this.cachedBounds) {
                        this.updateCachedBounds();
                    }

                    const bounds = this.cachedBounds;
                    if (bounds && (e.clientX < bounds.left || e.clientX > bounds.right || e.clientY < bounds.top || e.clientY > bounds.bottom)) {
                        this.closePanel();
                    }
                });
            }
        }, { passive: true });

        // Listen for custom category selection event
        window.addEventListener('category:selected', (e) => {
            if (e.detail) this.openPanel(e.detail);
        });
    }

    async handleCategoryHover(item) {
        if (!item) return;

        if (!item.children?.length) {
            this.showSpinner();
            try {
                const baseUrl = window.__categoriesTreeUrl || '/api/categories/tree';
                const res = await fetch(`${baseUrl}?parent_id=${item.id}`);
                const data = await res.json();
                item.children = data.data || [];
            } catch (err) {
                console.error('[CategoryPanel] Fetch tree error:', err);
            }
        }
        this.emitCategorySelected(item);
    }

    emitCategorySelected(item) {
        window.__lastSelectedCategory = item;
        try {
            localStorage.setItem('lastSelectedCategory', JSON.stringify(item));
        } catch {}
        window.dispatchEvent(new CustomEvent('category:selected', { detail: item }));
    }

    openPanel(category) {
        const { panel, title, body, actions, promoLink } = this.elements;
        if (!panel || !body) return;

        const children = this.resolveChildren(category);

        if (title) {
            title.textContent = (category?.name || 'Podkategorie').toUpperCase();
        }

        body.innerHTML = children.length
            ? children.map(child => this.renderCategoryCard(child)).join('')
            : '<div class="w-full text-center text-lg text-white font-light">Brak podkategorii w tej sekcji.</div>';

        if (category?.id && promoLink && actions) {
            const baseUrl = window.__promoBaseUrl || '/search';
            promoLink.href = `${baseUrl}?promotions=1&category_id=${encodeURIComponent(category.id)}`;
            actions.classList.remove('hidden');
        }

        panel.classList.remove('hidden');
        panel.setAttribute('data-open', 'true');
        this.activeCategoryId = category?.id;
        requestAnimationFrame(() => this.updateCachedBounds());
        this.updateActiveIndicators(category?.id);
    }

    closePanel() {
        const { panel, categoryContainer, categoryActiveBar, carouselActiveBar } = this.elements;
        if (!panel) return;

        panel.classList.add('hidden');
        panel.setAttribute('data-open', 'false');
        this.activeCategoryId = null;
        this.cachedBounds = null;

        if (categoryContainer) {
            categoryContainer.querySelectorAll('button[data-cat-id]').forEach(btn => {
                btn.classList.remove('font-bold', 'is-active');
            });
        }
        if (categoryActiveBar) categoryActiveBar.classList.add('hidden');
        if (carouselActiveBar) carouselActiveBar.classList.add('hidden');
    }

    updateActiveIndicators(catId) {
        const { categoryContainer, carouselActiveBar, carouselCol } = this.elements;
        if (!categoryContainer || !catId) return;

        const buttons = categoryContainer.querySelectorAll('button[data-cat-id]');
        buttons.forEach(btn => btn.classList.remove('font-bold', 'is-active'));

        const activeBtn = categoryContainer.querySelector(`button[data-cat-id="${catId}"]`);
        if (!activeBtn) return;

        activeBtn.classList.add('font-bold', 'is-active');

        if (carouselActiveBar && carouselCol) {
            // Decouple geometry reads from style mutations via requestAnimationFrame to eliminate forced reflows
            requestAnimationFrame(() => {
                const btnRect = activeBtn.getBoundingClientRect();
                const colRect = carouselCol.getBoundingClientRect();
                const topOffset = Math.max(0, btnRect.top - colRect.top);

                carouselActiveBar.style.top = `${topOffset}px`;
                carouselActiveBar.style.height = `${btnRect.height}px`;
                carouselActiveBar.classList.remove('hidden');
            });
        }
    }

    renderCategoryCard(child) {
        const url = child.slug ? `/${child.slug}` : (child.id ? `/categories/${child.id}` : '#');
        const imgUrl = child.logo_url || child.image_url;
        const name = child.name || '';
        const initial = name.charAt(0).toUpperCase();

        const imageContent = imgUrl
            ? `<img src="${imgUrl}" alt="${name}" loading="lazy" width="120" height="120" class="max-w-full max-h-full object-contain" />`
            : `<div class="text-4xl text-gray-300 font-bold">${initial}</div>`;

        return `
            <a href="${url}" class="w-full group block text-center py-2">
                <div class="aspect-square bg-white rounded-lg shadow-2xl flex items-center justify-center p-4 transition-all duration-300 group-hover:scale-105 overflow-hidden">
                    ${imageContent}
                </div>
                <div class="mt-4 text-sm md:text-base font-light text-white group-hover:text-primary transition-colors leading-tight h-[40px] flex items-start justify-center">
                    ${name}
                </div>
            </a>
        `;
    }

    resolveChildren(category) {
        if (category?.children?.length) return category.children;

        try {
            const cached = JSON.parse(localStorage.getItem('categories')) || [];
            const match = cached.find(c => String(c.id) === String(category?.id) || c.name?.toLowerCase() === category?.name?.toLowerCase());
            return match?.children || [];
        } catch {
            return [];
        }
    }

    showSpinner() {
        const { body, panel } = this.elements;
        if (body) {
            body.innerHTML = `
                <div class="w-full text-center py-10">
                    <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
            `;
        }
        if (panel) panel.classList.remove('hidden');
    }

    scrollSubcategories(direction) {
        const { body } = this.elements;
        if (body) {
            body.scrollBy({ left: direction * body.clientWidth * 0.8, behavior: 'smooth' });
        }
    }
}

export function initHomeCategories() {
    const manager = new CategoryPanelManager();
    manager.init();
}
